import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { HERO } from "@/constants/testIds";
import { ArrowUpRight, Sparkles, BadgeCheck, Mic, Plus } from "lucide-react";
import Magnetic from "./Magnetic";
import CountUp from "./CountUp";

/* ── Interactive demo data ────────────────────────────────────────────── */
const ROLES = [
  { label: "Frontend Engineer", score: 86, skills: 74, comms: 91, note: "Strong React depth — now sharpen your system-design stories." },
  { label: "Data Scientist",    score: 79, skills: 88, comms: 70, note: "Excellent ML fundamentals — practice stakeholder framing." },
  { label: "Product Manager",   score: 83, skills: 76, comms: 94, note: "Crisp narrative — quantify impact with hard metrics." },
  { label: "UX Designer",       score: 81, skills: 80, comms: 85, note: "Great portfolio signal — add measurable outcomes." },
];

const ROTATING = ["colleges", "students", "placement cells"];

/* ── Kinetic word that cycles ─────────────────────────────────────────── */
function RotatingWord() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % ROTATING.length), 2600);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="relative inline-block align-baseline">
      <AnimatePresence mode="wait">
        <motion.span
          key={ROTATING[i]}
          initial={{ y: "0.5em", opacity: 0, filter: "blur(6px)" }}
          animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
          exit={{ y: "-0.5em", opacity: 0, filter: "blur(6px)" }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 inline-block"
        >
          {ROTATING[i]}
        </motion.span>
      </AnimatePresence>
      <span className="absolute left-0 right-0 bottom-1.5 h-3 md:h-4 bg-[#FEF3C7] -z-0 rounded-sm" />
    </span>
  );
}

/* ── Animated SVG score ring ──────────────────────────────────────────── */
function AnimatedRing({ value, size = 230 }) {
  const stroke = 14;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const mv = useMotionValue(c);
  const spring = useSpring(mv, { stiffness: 60, damping: 16 });
  useEffect(() => {
    mv.set(c - (value / 100) * c);
  }, [value, c, mv]);

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
      <defs>
        <linearGradient id="wsh-arc" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#D97706" />
          <stop offset="55%" stopColor="#C2410C" />
          <stop offset="100%" stopColor="#15803D" />
        </linearGradient>
      </defs>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EFEAE0" strokeWidth={stroke} />
      <motion.circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="url(#wsh-arc)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={c}
        style={{ strokeDashoffset: spring }}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

export default function Hero() {
  const [active, setActive] = useState(0);
  const [touched, setTouched] = useState(false);
  const role = ROLES[active];

  // Auto-cycle the demo until the user interacts
  useEffect(() => {
    if (touched) return;
    const t = setInterval(() => setActive((v) => (v + 1) % ROLES.length), 3400);
    return () => clearInterval(t);
  }, [touched]);

  // Mouse parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });
  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - (r.left + r.width / 2)) / r.width);
    my.set((e.clientY - (r.top + r.height / 2)) / r.height);
  };

  // Parallax transforms for floating elements
  const spotX = useTransform(sx, [-0.5, 0.5], ["-8%", "8%"]);
  const spotY = useTransform(sy, [-0.5, 0.5], ["-8%", "8%"]);
  const cardTiltX = useTransform(sy, [-0.5, 0.5], [8, -8]);
  const cardTiltY = useTransform(sx, [-0.5, 0.5], [-10, 10]);
  const chipA = { x: useTransform(sx, [-0.5, 0.5], [18, -18]), y: useTransform(sy, [-0.5, 0.5], [14, -14]) };
  const chipB = { x: useTransform(sx, [-0.5, 0.5], [-22, 22]), y: useTransform(sy, [-0.5, 0.5], [-12, 12]) };

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
  };
  const rise = {
    hidden: { opacity: 0, y: 22, filter: "blur(8px)" },
    show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <section
      id="top"
      onMouseMove={onMouseMove}
      className="relative pt-32 md:pt-40 pb-12 md:pb-24 wsh-grain overflow-hidden"
    >
      {/* Aurora background that follows the cursor */}
      <div className="absolute inset-0 -z-10" style={{ background: "#FDFBF7" }} />
      <motion.div
        className="absolute -z-10 rounded-full blur-[90px] opacity-60"
        style={{
          width: 720, height: 520, left: "55%", top: "-12%", x: spotX, y: spotY,
          background: "radial-gradient(circle at 50% 50%, rgba(217,119,6,0.22), transparent 62%)",
        }}
      />
      <motion.div
        className="absolute -z-10 rounded-full blur-[90px] opacity-50"
        style={{
          width: 620, height: 480, left: "-12%", top: "28%", x: spotY, y: spotX,
          background: "radial-gradient(circle at 50% 50%, rgba(21,128,61,0.16), transparent 62%)",
        }}
      />

      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        {/* Left column */}
        <motion.div
          className="lg:col-span-7"
          variants={container}
          initial="hidden"
          animate="show"
        >
          <motion.span variants={rise} className="tag">
            <Sparkles size={12} /> AI placement readiness · built for campus
          </motion.span>

          <h1 className="font-display mt-6 text-[2.8rem] sm:text-[3.4rem] md:text-[5rem] leading-[0.95] tracking-tight">
            <motion.span variants={rise} className="block">
              Placement readiness{" "}
              <span className="italic font-normal text-stone-600">for</span>
            </motion.span>
            <motion.span variants={rise} className="block mt-1">
              <RotatingWord />
              <span className="italic font-normal text-stone-600">.</span>
            </motion.span>
          </h1>

          <motion.p
            variants={rise}
            className="mt-7 text-lg md:text-xl text-stone-700 max-w-xl leading-relaxed"
          >
            Wishire scores every student&apos;s job-readiness — résumé, AI voice
            interview, proctored tests and communication — so your placement cell
            runs drives on proof, and every student knows exactly what to fix next.
          </motion.p>

          <motion.div variants={rise} className="mt-9 flex flex-col sm:flex-row gap-3">
            <Magnetic strength={0.4}>
              <a href="#book-demo" data-testid={HERO.companyCta} className="btn-primary w-full sm:w-auto justify-center">
                Book a demo for your college <ArrowUpRight size={16} />
              </a>
            </Magnetic>
            <Magnetic strength={0.4}>
              <a href="#for-students" data-testid={HERO.candidateCta} className="btn-ghost w-full sm:w-auto justify-center">
                I&apos;m a student <ArrowUpRight size={16} />
              </a>
            </Magnetic>
          </motion.div>

          <motion.div variants={rise} className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-stone-600">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-600 wsh-pulse" />
              <span className="font-mono text-xs uppercase tracking-widest">Live · Building in public</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <BadgeCheck size={16} className="text-[#15803D]" /> One objective readiness score
            </div>
          </motion.div>
        </motion.div>

        {/* Right column — interactive score studio */}
        <motion.div
          className="lg:col-span-5 relative"
          data-testid={HERO.scoreCard}
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          style={{ perspective: 1200 }}
        >
          <motion.div
            style={{ rotateX: cardTiltX, rotateY: cardTiltY, transformStyle: "preserve-3d" }}
            className="glass rounded-[2rem] p-6 md:p-7 relative"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="overline">Live Wishire Score</span>
              <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-700">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600 wsh-pulse" /> analyzing
              </span>
            </div>

            {/* Ring with animated number */}
            <div className="relative flex items-center justify-center" style={{ minHeight: 230 }}>
              <AnimatedRing value={role.score} size={230} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="overline text-[10px]">Wishire</span>
                <span className="font-display text-6xl leading-none mt-1">
                  <CountUp value={role.score} />
                </span>
                <span className="text-stone-400 text-sm font-mono">/ 100</span>
              </div>
            </div>

            {/* Sub-metrics */}
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <Metric label="Readiness" value={role.score} color="#C2410C" />
              <Metric label="Skills" value={role.skills} color="#15803D" />
              <Metric label="Comms" value={role.comms} color="#D97706" />
            </div>

            {/* AI note */}
            <div className="mt-4 rounded-2xl bg-white/70 border border-stone-200/70 px-4 py-3 min-h-[60px] flex items-center gap-2">
              <Sparkles size={14} className="text-[#C2410C] shrink-0" />
              <AnimatePresence mode="wait">
                <motion.p
                  key={role.note}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.4 }}
                  className="text-sm text-stone-700 leading-snug"
                >
                  {role.note}
                </motion.p>
              </AnimatePresence>
            </div>

            {/* Role selector — the interactive bit */}
            <div className="mt-4">
              <p className="overline mb-2 text-[10px]">Try a role →</p>
              <div className="flex flex-wrap gap-2">
                {ROLES.map((r, i) => (
                  <button
                    key={r.label}
                    onClick={() => { setActive(i); setTouched(true); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-300 border ${
                      active === i
                        ? "bg-stone-900 text-stone-50 border-stone-900 scale-105"
                        : "bg-white/60 text-stone-600 border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Floating verified chip */}
          <motion.div
            style={chipA}
            className="absolute -left-3 md:-left-8 top-6 glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-lg"
          >
            <BadgeCheck size={20} className="text-[#15803D]" />
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Proctored</p>
              <p className="text-sm text-stone-900">TCS NQT pattern · integrity ✓</p>
            </div>
          </motion.div>

          {/* Floating voice-AI chip */}
          <motion.div
            style={chipB}
            className="absolute -right-2 md:-right-6 bottom-8 glass rounded-2xl px-4 py-3 flex items-center gap-3 shadow-lg"
          >
            <div className="flex items-end h-5">
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className="wsh-bar" style={{ height: 20, background: "#1C1917", animationDelay: `${i * 130}ms` }} />
              ))}
            </div>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest text-stone-500">Voice AI</p>
              <p className="text-sm text-stone-900">Mock interview · live</p>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Trust marquee */}
      <div className="mt-16 md:mt-24 relative">
        <div className="container-x">
          <p className="overline text-center mb-6">Built for India&apos;s placement cells · trusted signals</p>
        </div>
        <div className="relative overflow-hidden wsh-no-scrollbar [mask-image:linear-gradient(90deg,transparent,#000_12%,#000_88%,transparent)]">
          <div className="wsh-marquee-track py-2">
            {[...MARQUEE, ...MARQUEE].map((t, i) => (
              <span key={i} className="font-display text-2xl md:text-3xl text-stone-400 whitespace-nowrap flex items-center gap-4">
                {t} <span className="text-[#C2410C]">✦</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const MARQUEE = [
  "Batch Readiness",
  "Campus Drives",
  "Wishire Score",
  "AI Interviews",
  "Placement Reports",
  "Verified Students",
];

function Metric({ label, value, color }) {
  return (
    <div className="rounded-xl bg-white/50 border border-stone-200/60 py-2">
      <p className="font-display text-xl" style={{ color }}>
        <CountUp value={value} />
      </p>
      <p className="overline mt-0.5 text-[9px]">{label}</p>
    </div>
  );
}
